// Adminkundlistans nya kolumner: telefon · senaste bokning · obetalt (010).
//
// Aggregaten räknas i `get_customers_admin_list` (ETT anrop). Här ligger bara
// presentationen och pagineringsmatten — det som går att pröva utan databas.
//
// VARFÖR AGGREGATEN INTE LIGGER I KLIENTEN: `customers.last_booking_at`,
// `total_bookings` och `total_spent*` är ifyllda för 0 av 5750 AP-rader
// (FEL-013 — `update_customer_stats()` har ingen anropare), så 7 av 11
// sorteringsalternativ var tysta no-ops. Värdena måste räknas fram, och de måste
// räknas fram i DB: listan är serverpaginerad, så en klient-join kan bara
// dekorera de 20 rader som redan hämtats — sortering och filtrering på ett
// framräknat fält vore då omöjlig.
import { format } from "date-fns";
import { sv } from "date-fns/locale";

/** En rad som `get_customers_admin_list` returnerar. */
export interface CustomerListRow {
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  customer_number: number | null;
  user_id: string | null;
  category_id: string | null;
  customer_type: string | null;
  gender: string | null;
  source: string | null;
  is_archived: boolean | null;
  blocked: boolean | null;
  avatar_url: string | null;
  matchi_customer_nr: string | null;
  created_at: string;
  first_booking_at: string | null;
  last_booking_at: string | null;
  bookings_count: number;
  spent_minor: number;
  unpaid_count: number;
  unpaid_minor: number;
  total_count: number;
}

/** Platshållare när ett värde saknas — samma tecken i alla tre kolumnerna. */
export const TOM = "—";

/**
 * Telefon som den ska visas. Tomma strängar räknas som saknat värde: 3339 av
 * AP:s 5750 kunder saknar telefon, och `""` ska inte renderas som en tom lucka
 * utan som ett streck.
 */
export function formatPhone(phone: string | null | undefined): string {
  const t = (phone ?? "").trim();
  return t === "" ? TOM : t;
}

/**
 * Senaste bokning. Bär årtal — kolumnen läses långt efter att raden skrevs, och
 * ett årlöst datum i en lista som spänner flera år går inte att tolka
 * ([Lesson 100]-familjen: fryst/långlivad text måste bära år).
 *
 * Månadsnamnet följer gränssnittets språk (`i18n.language`) — samma mönster som
 * `CancellationPolicyLine`: svensk locale för "sv*", annars date-fns standard
 * (engelska). Hårdkodad sv gav "17 okt. 2026" i den engelska vyn.
 */
export function formatLastBooking(value: string | null | undefined, language: string | undefined): string {
  if (!value) return TOM;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return TOM;
  return format(d, "d MMM yyyy", language?.startsWith("sv") ? { locale: sv } : undefined);
}

/**
 * Obetalt saldo i öre → visningssträng.
 *
 * 0 är INTE samma sak som saknat: en kund utan skuld ska inte se ett streck som
 * antyder "vet ej". Men vi visar heller inte "0 kr" på 5748 rader — det vore
 * brus. Noll ⇒ streck, positivt ⇒ belopp.
 */
export function formatUnpaid(minor: number | null | undefined): string {
  const v = minor ?? 0;
  if (v <= 0) return TOM;
  return `${Math.round(v / 100).toLocaleString("sv-SE")} kr`;
}

/** True när kunden har en skuld värd att driva in. */
export function hasUnpaid(row: Pick<CustomerListRow, "unpaid_minor">): boolean {
  return (row.unpaid_minor ?? 0) > 0;
}

/**
 * Sidräkning ur `total_count`.
 *
 * Före 010 fanns inget totalantal: "Nästa" inaktiverades heuristiskt av
 * `customers.length < pageSize`, vilket gissar fel på exakt full sista sida.
 * `count(*) over ()` ger sanningen i samma anrop.
 */
export function paginationState(opts: {
  page: number;
  pageSize: number;
  rows: number;
  totalCount: number;
}): { from: number; to: number; total: number; hasPrev: boolean; hasNext: boolean } {
  const { page, pageSize, rows, totalCount } = opts;
  const from = totalCount === 0 ? 0 : page * pageSize + 1;
  const to = page * pageSize + rows;
  return {
    from,
    to,
    total: totalCount,
    hasPrev: page > 0,
    hasNext: to < totalCount,
  };
}
